<template>
  <Teleport to=".db-app">
    <div
      class="db-wrapper-modal"
      :class="$attrs.class"
    >
      <div
        ref="dialogRef"
        class="db-modal"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="`modal-title-${uid}`"
      >
        <div class="db-modal-header">
          <div
            :id="`modal-title-${uid}`"
            class="db-modal-header-content"
          >
            <slot name="header" />
          </div>
          <button
            class="db-button-square db-modal-close"
            v-on:click="$emit('close-modal')"
          >
            <IconDbClose
              width="12"
              height="12"
            />
            <span class="db-visually-hidden">{{ $t('Close') }}</span>
          </button>
        </div>
        <div class="db-modal-content db-shadow-scrollbox">
          <slot />
        </div>
        <div
          v-if="$slots.footer"
          class="db-modal-footer"
        >
          <slot name="footer" />
        </div>
      </div>
      <!-- end modal-->
    </div>
    <!-- end wrapper-->
  </Teleport>
</template>

<script setup lang="ts">
import { v4 as uuidv4 } from 'uuid'
import { ref, onMounted, onUnmounted } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'

const props = withDefaults(
  defineProps<{
    /** Add `db-noscroll` class to `<html>` element */
    lockScroll?: boolean
  }>(),
  {
    lockScroll: true,
  },
)

const emit = defineEmits<{
  /** Ask for modal closing */
  (e: 'close-modal'): void
  /** emited when mounted */
  (e: 'has-opened'): void
  /** emited when unmounted */
  (e: 'has-closed'): void
}>()

/** The modal root element */
const dialogRef = ref<HTMLElement | null>(null)

const focusTrapOptions = {
  /** Set immediatly the focus on the first focusable element */
  immediate: true,
  /** Escape key pressed */
  escapeDeactivates: () => {
    emit('close-modal')
    return true
  },
}

const { activate, deactivate } = useFocusTrap(dialogRef, focusTrapOptions)

/** Unique id for aria binding */
const uid = uuidv4()

/** Init clickoutside module, focus-trap and set class on html */
onMounted(() => {
  emit('has-opened')

  if (props.lockScroll) {
    /** Set up no scroll on document */
    const htmlElement = document.querySelector('html')
    htmlElement?.classList.toggle('db-noscroll')
    htmlElement?.classList.toggle('db-modal-backdrop')
  }

  onClickOutside(dialogRef, () => emit('close-modal'))
  activate
})

/** Deactivate focus-trap and remove class on html */
onUnmounted(() => {
  emit('has-closed')

  /** Remove scrollock and backdrop */
  const htmlElement = document.querySelector('html')
  htmlElement?.classList.remove('db-noscroll')
  htmlElement?.classList.remove('db-modal-backdrop')

  deactivate
})
</script>

<style>
@import '@/assets/styles/components/wrappers/_modal.css';
</style>
